import { useState, useEffect, useRef } from "react";
import {
  Table,
  Tabs,
  Select,
  Button,
  DatePicker,
  Dropdown,
  Space,
  Pagination,
  Form,
  Spin,
  message,
} from "antd";
import {
  DownOutlined,
  ArrowRightOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { CSVLink } from "react-csv";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { CALL_API } from "../../../Common/api";
import ExportIcon from "../../../assets/images/export-svgrepo-com (1).svg";
import { getOverviewCSVData, getDetailedLogsCSVData } from "./mileageCSVExport";

const { Option } = Select;
const { RangePicker } = DatePicker;

let columns = [
  {
    title: "Vehicle Name",
    dataIndex: "vehicleName",
    key: "vehicleName",
    fixed: "left",
    width: 140,
    render: (text) => (
      <span style={{ color: "#555", fontWeight: 500, fontSize: 13 }}>
        {text}
      </span>
    ),
  },
  {
    title: "Total Distance",
    dataIndex: "totalDistance",
    key: "totalDistance",
    width: 130,
    render: (val) => <span style={{ color: "#555", fontSize: 13 }}>{val}</span>,
  },
  {
    title: "Active Days",
    dataIndex: "activeDays",
    key: "activeDays",
    width: 110,
    render: (val) => <span style={{ color: "#555", fontSize: 13 }}>{val}</span>,
  },
];

const DetailedLogsColumns = [
  {
    title: "Vehicle Name",
    dataIndex: "Regno",
    key: "Regno",
    width: 180,
    fixed: "left",
  },
  {
    title: "Device ID",
    dataIndex: "FAssetID",
    key: "FAssetID",
    width: 160,
  },
  {
    title: "Date",
    dataIndex: "FDate",
    key: "FDate",
    width: 150,
    render: (val) => <span>{dayjs(val).format("DD/MM/YYYY")}</span>,
  },
  {
    title: "Total Distance",
    dataIndex: "FTotalMileage",
    key: "FTotalMileage",
    width: 180,
  },
  {
    title: "Start Mileage",
    dataIndex: "FStartMileage",
    key: "FStartMileage",
    width: 180,
  },
  {
    title: "End Mileage",
    dataIndex: "FEndMileage",
    key: "FEndMileage",
    width: 180,
  },
];

const tabItems = [
  { key: "overview", label: "Overview" },
  { key: "detailed", label: "Detailed Logs" },
];

const Duration = [
  { label: "Current Month", value: "current_month" },
  { label: "Last Month", value: "last_month" },
  { label: "Today", value: "today" },
];

export default function MileageReport() {
  const [activeTab, setActiveTab] = useState("overview");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const AIVehicleLists = useSelector(
    (state) => state?.authTracking?.AIVehicleLists,
  );
  const [selectedDate, setSelectedDate] = useState(null);
  const [duration, setDuration] = useState("current_month");
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [mileageOverview, setMileageOverview] = useState([]);
  const [mileageDetailedLogs, setMileageDetailedLogs] = useState([]);
  const [totalMileageOverview, setTotalMileageOverview] = useState(0);
  const [totalMileageDetailedLogs, setTotalMileageDetailedLogs] = useState(0);
  const [loading, setLoading] = useState(false);
  const [overViewColumns, setOverViewColumns] = useState([]);
  const [CSVLoader, setCSVLoader] = useState(false);
  const [CSVList, setCSVList] = useState([]);
  const csvLinkEl = useRef();

  useEffect(() => {
    let startDate = null;
    let endDate = null;

    if (duration === "today") {
      startDate = dayjs();
      endDate = dayjs();
    } else if (duration === "current_month") {
      startDate = dayjs().startOf("month");
      endDate = dayjs().endOf("month");
    } else if (duration === "last_month") {
      startDate = dayjs().subtract(1, "month").startOf("month");
      endDate = dayjs().subtract(1, "month").endOf("month");
    } else if (duration === "current_year") {
      startDate = dayjs().startOf("year");
      endDate = dayjs().endOf("year");
    }

    if (startDate && endDate) {
      setSelectedDate([startDate, endDate]);
    }
  }, [duration]);

  const getDailyMileageOverview = async () => {
    const startDate = selectedDate?.[0]
      ? dayjs(selectedDate[0]).format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD");

    const endDate = selectedDate?.[1]
      ? dayjs(selectedDate[1]).format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD");

    try {
      setLoading(true);

      const { data } = await CALL_API(
        "POST",
        "ai/trackinglink/getDailyMileageOverview",
        {
          startDate,
          endDate,
          vehicleRegNo: selectedVehicle || "",
          page: currentPage,
          pageSize: pageSize,
        },
      );

      setTotalMileageOverview(data?.data?.total);
      let dateColumns = [];
      let dates = data?.data?.data[0]?.dates || [];
      for (let i = 0; i < dates.length; i++) {
        dateColumns.push({
          title: dates[i].date,
          dataIndex: dates[i].date,
          key: dates[i].date,
          width: 150,
          render: (val) => <span>{val}</span>,
        });
      }

      let viewData = [];

      let values = data?.data?.data || [];
      for (let i = 0; i < values.length; i++) {
        let obj = values[i];
        let pushData = {};

        for (let j = 0; j < obj.dates.length; j++) {
          let date = obj.dates[j];
          pushData[`${date.date}`] = date.distance;
        }
        pushData.activeDays = obj.activeDays;
        pushData.totalDistance = obj.TotalDistance;
        pushData.vehicleName = obj.vehicleName;

        viewData.push(pushData);
      }

      setOverViewColumns([...columns, ...dateColumns]);
      setMileageOverview(viewData);
    } catch (error) {
      console.log("Error fetching daily mileage overview:", error);
      setMileageOverview([]);
    } finally {
      setLoading(false);
    }
  };
  const getMileageDetailedLogs = async () => {
    const startDate = selectedDate?.[0]
      ? dayjs(selectedDate[0]).format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD");

    const endDate = selectedDate?.[1]
      ? dayjs(selectedDate[1]).format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD");
    setLoading(true);
    const { data } = await CALL_API(
      "POST",
      "ai/trackinglink/getMileageDetailedLogs",
      {
        startDate: startDate,
        endDate: endDate,
        vehicleRegNo: selectedVehicle || "",
        page: currentPage,
        pageSize: pageSize,
      },
    );
    setMileageDetailedLogs(data?.data?.data);
    setTotalMileageDetailedLogs(data?.data?.total);
    setLoading(false);
  };

  const downloadCSV = (csvData) => {
    if (csvData.length > 0) {
      setCSVList(csvData);

      setTimeout(() => {
        csvLinkEl.current.link.click();
      }, 500);
    } else {
      message.error("No data available to download.");
    }
  };

  const prepareCSV = async () => {
    try {
      setCSVLoader(true);

      const startDate = selectedDate?.[0]
        ? dayjs(selectedDate[0]).format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD");

      const endDate = selectedDate?.[1]
        ? dayjs(selectedDate[1]).format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD");

      if (activeTab === "overview") {
        const { data } = await CALL_API(
          "POST",
          "ai/trackinglink/getDailyMileageOverview",
          {
            startDate,
            endDate,
            vehicleRegNo: selectedVehicle || "",
            page: 1,
            pageSize: totalMileageOverview,
          },
        );

        downloadCSV(getOverviewCSVData(data?.data?.data || []));
      } else {
        const { data } = await CALL_API(
          "POST",
          "ai/trackinglink/getMileageDetailedLogs",
          {
            startDate,
            endDate,
            vehicleRegNo: selectedVehicle || "",
            page: 1,
            pageSize: totalMileageDetailedLogs,
          },
        );

        downloadCSV(getDetailedLogsCSVData(data?.data?.data || []));
      }
    } catch (error) {
      console.error("CSV Download Error:", error);
      message.error("Failed to download CSV");
    } finally {
      setCSVLoader(false);
    }
  };

  useEffect(() => {
    if (activeTab === "overview") {
      getDailyMileageOverview();
    } else {
      getMileageDetailedLogs();
    }
  }, [selectedDate, currentPage, pageSize, selectedVehicle, activeTab]);
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 16,
        boxShadow: "0 2px 24px rgba(0,0,0,0.08)",
        padding: "0 0 0 0",
        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
        margin: "32px auto",
        overflow: "hidden",
        border: "1px solid #f0f0f0",
      }}
    >
      {/* Top Tab Bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "18px 28px 0 28px",
          borderBottom: "1px solid #f0f0f0",
          gap: 8,
        }}
      >
        <button
          style={{
            background: "none",
            border: "none",
            fontSize: 14,
            color: "#888",
            fontWeight: 500,
            cursor: "pointer",
            padding: "6px 14px",
            borderRadius: 20,
          }}
          onClick={() => navigate("/AIvehiclereport")}
        >
          Vehicle Report
        </button>
        <button
          style={{
            background: "#e8f5e9",
            border: "none",
            fontSize: 14,
            color: "#3a8f3a",
            fontWeight: 600,
            cursor: "pointer",
            padding: "6px 18px",
            borderRadius: 20,
          }}
        >
          Mileage Report
        </button>
      </div>

      {/* Filters Row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          padding: "18px 28px 10px 28px",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <Select
          defaultValue="All Vehicles"
          suffixIcon={<DownOutlined style={{ color: "#888", fontSize: 11 }} />}
          style={{ width: 130, borderRadius: 20 }}
          options={AIVehicleLists?.map((item) => ({
            label: item.FVehicleName,
            value: item.FVehicleName,
          }))}
          onChange={(value) => {
            setSelectedVehicle(value);
          }}
          filterOption={(input, option) =>
            option?.label?.toLowerCase().includes(input.toLowerCase())
          }
          allowClear
          styles={{
            selector: {
              borderRadius: 20,
              border: "1px solid #e0e0e0",
              background: "#fff",
              fontSize: 13,
            },
          }}
        />{" "}
        {/* Period Dropdown */}{" "}
        <Select
          value={duration}
          onChange={(value) => {
            setDuration(value);
          }}
          suffixIcon={<DownOutlined style={{ color: "#888", fontSize: 11 }} />}
          style={{
            width: 150,
            borderRadius: 20,
            fontSize: 13,
          }}
        >
          {Duration.map((item) => (
            <Option key={item.value} value={item.value}>
              {item.label}
            </Option>
          ))}
        </Select>
        <RangePicker
          value={selectedDate}
          onChange={setSelectedDate}
          disabledDate={(current, info) => {
            if (!info?.from) return false;

            return current.diff(info.from, "day") > 92;
          }}
        />
        <div>
          <Spin spinning={CSVLoader}>
            <img
              src={ExportIcon}
              style={{ cursor: "pointer" }}
              alt="Upload Icon"
              className="hover-image"
              onClick={() => {
                // if (!exportAllowed) return;
                prepareCSV();
              }}
            />
          </Spin>
          <CSVLink
            data={CSVList}
            filename={`${activeTab === "overview" ? "Mileage_Overview" : "Mileage_Detailed_Logs"}.csv`}
            asyncOnClick={true}
            ref={csvLinkEl}
          />
        </div>
      </div>

      {/* Sub Tabs: Overview / Detailed Logs */}
      <div style={{ padding: "0 28px" }}>
        <div
          style={{ display: "flex", gap: 0, borderBottom: "2px solid #f0f0f0" }}
        >
          {tabItems.map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                setCurrentPage(1);
                setPageSize(10);
              }}
              style={{
                background: "none",
                border: "none",
                borderBottom:
                  activeTab === tab.key
                    ? "2.5px solid #4caf50"
                    : "2.5px solid transparent",
                color: activeTab === tab.key ? "#4caf50" : "#888",
                fontWeight: activeTab === tab.key ? 600 : 400,
                fontSize: 14,
                cursor: "pointer",
                padding: "10px 4px",
                marginRight: 24,
                marginBottom: -2,
                transition: "all 0.2s",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {activeTab === "overview" && (
        <div style={{ padding: "0 0 0 0" }}>
          <div
            style={{
              border: "1px solid #F0F0F0",
              borderRadius: 12,
              overflow: "hidden",
            }}
          >
            <Table
              columns={overViewColumns}
              dataSource={mileageOverview}
              scroll={{ x: "max-content", y: "calc(100vh - 300px)" }}
              pagination={false}
              bordered={false}
              rowClassName={() => "custom-row"}
              size="middle"
              style={{ fontSize: 13 }}
              loading={loading}
            />
          </div>

          {/* Footer */}
          <div
            style={{
              marginTop: 24,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 16,
              padding: "0 10px",
            }}
          >
            <div style={{ color: "#7A7A7A", fontWeight: 500 }}>
              Showing {(currentPage - 1) * pageSize + 1} –
              {Math.min(currentPage * pageSize, totalMileageOverview)} of{" "}
              {totalMileageOverview}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 18,
              }}
            >
              <div style={{ color: "#7A7A7A" }}>
                Rows per page:{" "}
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: "#7A7A7A",
                    cursor: "pointer",
                  }}
                >
                  {[10, 20, 50].map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              <Pagination
                current={currentPage}
                total={totalMileageOverview}
                pageSize={pageSize}
                showSizeChanger={false}
                onChange={(page) => setCurrentPage(page)}
              />
            </div>
          </div>
        </div>
      )}
      {activeTab === "detailed" && (
        <>
          <div
            style={{
              border: "1px solid #F0F0F0",
              borderRadius: 12,
              overflow: "hidden",
            }}
          >
            <Table
              columns={DetailedLogsColumns}
              dataSource={mileageDetailedLogs}
              pagination={false}
              scroll={{ x: 1100 }}
              bordered={false}
              rowClassName={() => "custom-row"}
              loading={loading}
            />
          </div>

          {/* Footer */}
          <div
            style={{
              marginTop: 24,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 16,
              padding: "0 10px",
            }}
          >
            <div
              style={{
                color: "#7A7A7A",
                fontWeight: 500,
              }}
            >
              Showing {(currentPage - 1) * pageSize + 1} –
              {Math.min(currentPage * pageSize, totalMileageDetailedLogs)} of{" "}
              {totalMileageDetailedLogs}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 18,
              }}
            >
              <div style={{ color: "#7A7A7A" }}>
                Rows per page:
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: "#7A7A7A",
                    cursor: "pointer",
                  }}
                >
                  {[10, 20, 50].map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              <Pagination
                current={currentPage}
                total={totalMileageDetailedLogs}
                pageSize={pageSize}
                showSizeChanger={false}
                onChange={(page) => setCurrentPage(page)}
              />
            </div>
          </div>

          {/* Custom Styles */}
          <style>
            {`
          .ant-table-thead > tr > th {
            background: #F8F8F8 !important;
            font-weight: 600;
            color: #2B2B2B;
            border-bottom: 1px solid #F0F0F0 !important;
          }

          .ant-table-tbody > tr > td {
            padding-top: 22px !important;
            padding-bottom: 22px !important;
            color: #60708F;
            border-bottom: 1px solid #F3F3F3 !important;
          }

          .custom-row:hover td {
            background: #FAFAFA !important;
          }

          .ant-tabs-tab-active .ant-tabs-tab-btn {
            color: #2BA84A !important;
            font-weight: 600;
          }

          .ant-tabs-ink-bar {
            background: #2BA84A !important;
          }

          .ant-pagination-item-active {
            border-color: #2BA84A !important;
          }

          .ant-pagination-item-active a {
            color: #2BA84A !important;
          }
        `}
          </style>
        </>
      )}
    </div>
  );
}